import { FC, useMemo } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import { createColumnHelper } from '@tanstack/react-table';
import useAdminVerificationRequests, { VerificationRequest } from '@/api/useAdminVerificationRequests.ts';
import useUpdateVerification from '@/api/useUpdateVerification.ts';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';

const columnHelper = createColumnHelper<VerificationRequest>();

const AdminRequestsPage: FC = () => {
  const { data: requests, isLoading, isError } = useAdminVerificationRequests();
  const { mutate: updateVerification, isPending } = useUpdateVerification();
  const navigate = useNavigate();

  const handleAccept = (userId: string) => {
    updateVerification({ user_id: userId, is_verified: true });
  };

  const handleDecline = (userId: string) => {
    updateVerification({ user_id: userId, is_verified: false });
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'Carer',
        cell: (info) => (
          <div className="font-semibold">
            {info.getValue().split('@')[0]}
          </div>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Acc creation date',
        cell: (info) => dayjs(info.getValue()).format('MMMM DD, YYYY — hh:mm A'),
      }),
      columnHelper.display({
        id: 'details',
        header: 'Details',
        cell: (info) => (
          <button 
            onClick={() => navigate(Routes.ADMIN_CARER_DETAILS.replace(':carerId', info.row.original.id))}
            className="text-black hover:underline font-semibold"
          >
            Show details
          </button>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Action',
        cell: (info) => (
          <div className="flex gap-2">
            <button
              onClick={() => handleAccept(info.row.original.id)}
              disabled={isPending}
              className="text-[#00A96E] hover:opacity-70 font-semibold disabled:opacity-50"
            >
              Accept
            </button>
            <span className="text-gray-400">/</span>
            <button
              onClick={() => handleDecline(info.row.original.id)}
              disabled={isPending}
              className="text-[#FF5861] hover:opacity-70 font-semibold disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        ),
      }),
    ],
    [navigate, isPending]
  );

  return (
    <div className="flex flex-col gap-6 w-full max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lite admin dashboard for daily management</h1>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : isError ? (
        <div className="bg-red-50 rounded-lg p-8 text-center">
          <p className="text-red-500">Error loading requests</p>
        </div>
      ) : requests && requests.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-gray-500">No pending verification requests</p>
        </div>
      ) : (
        <DataTable data={requests || []} columns={columns} className="min-w-[540px]" />
      )}
    </div>
  );
};

export default AdminRequestsPage;
